import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, FileText, ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/Markdown";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export function GuideList({ guides }) {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);

  const launchTopic = (guide) => {
    const prompt = language === "hi" ? guide.prompt_hi : guide.prompt_en;
    navigate(`/?ask=${encodeURIComponent(prompt)}&mode=${guide.mode || "understand"}`);
  };

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {guides.map((guide) => {
          const title = language === "hi" ? guide.title_hi : guide.title_en;
          const summary = language === "hi" ? guide.summary_hi : guide.summary_en;
          const isArticle = guide.type === "article";
          return (
            <div key={guide.guide_id} data-testid={`guide-card-${guide.guide_id}`} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 transition-colors hover:border-primary/30">
              <span className={`inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${isArticle ? "bg-slate-100 text-slate-500" : "bg-primary/10 text-primary"}`}>
                {isArticle ? <FileText className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
                {isArticle ? t("overview") : t("ai_topic")}
              </span>
              <h3 className="font-display mt-2 text-base font-semibold text-slate-900">{title}</h3>
              <p className="mt-1 flex-1 text-sm leading-relaxed text-slate-500">{summary}</p>
              <div className="mt-4">
                {isArticle ? (
                  <Button variant="outline" size="sm" className="rounded-full" data-testid={`read-guide-${guide.guide_id}`} onClick={() => setArticle(guide)}>
                    {t("read_guide")}
                  </Button>
                ) : (
                  <Button size="sm" className="gap-1.5 rounded-full" data-testid={`launch-topic-${guide.guide_id}`} onClick={() => launchTopic(guide)}>
                    {t("launch_topic")} <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!article} onOpenChange={(o) => !o && setArticle(null)}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="article-dialog">
          {article && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">{language === "hi" ? article.title_hi : article.title_en}</DialogTitle>
                <DialogDescription>{language === "hi" ? article.summary_hi : article.summary_en}</DialogDescription>
              </DialogHeader>
              <Markdown content={language === "hi" ? article.body_hi : article.body_en} />
              <Button className="mt-2 w-full gap-1.5 rounded-full" data-testid="article-ask-btn"
                onClick={() => { const q = language === "hi" ? article.title_hi : article.title_en; navigate(`/?ask=${encodeURIComponent(q)}&mode=understand`); }}>
                <Sparkles className="h-4 w-4" /> {t("launch_topic")}
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
